import styled from '@emotion/styled'
import React, { cloneElement, MouseEvent, ReactElement, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useClickAway } from 'react-use'

interface TooltipProps {
  align?: 'top' | 'bottom'
  autoHideTooltip?: boolean
  autoShowTooltip?: boolean
  children: ReactElement
  className?: string
  disabled?: boolean
  id?: string
  isOnMouseOver?: boolean
  isToggledByClick?: boolean
  margin?: number
  text: string | JSX.Element
}

export function Tooltip({
  align = 'top',
  autoHideTooltip = true,
  autoShowTooltip = false,
  children,
  className = '',
  disabled,
  id,
  isOnMouseOver,
  isToggledByClick,
  margin = 12,
  text,
}: TooltipProps) {
  const [show, setShow] = useState(false)
  const [disableAutoTooltip, setDisableAutoTooltip] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const tooltipRootRef = useRef<HTMLElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  function handleTooltipOpen(e: MouseEvent) {
    e.preventDefault()
    e.stopPropagation()

    const viewportOffset = (e.target as HTMLElement).getBoundingClientRect()

    setPosition({
      x: viewportOffset.left + viewportOffset.width / 2,
      y: viewportOffset.top - viewportOffset.height,
    })

    setShow((prev) => !prev)
  }

  useClickAway(tooltipRef, (_e) => {
    setShow(false)
  })

  useEffect(() => {
    function handleScroll() {
      setShow(false)
    }

    document.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  })

  useEffect(() => {
    if (isToggledByClick && autoHideTooltip && show) {
      const timeout = setTimeout(() => {
        setShow(false)
        clearTimeout(timeout)
      }, 5000)
    }
  }, [show, isToggledByClick, autoHideTooltip])

  useEffect(() => {
    function handleTooltipAutoOpen() {
      if (id) {
        const viewportOffset = document.getElementById(id)?.getBoundingClientRect()
        if (viewportOffset) {
          setPosition({
            x: viewportOffset.left + viewportOffset.width / 2,
            y: viewportOffset.top - viewportOffset.height,
          })
          const timeout = setTimeout(() => {
            setShow(true)
            clearTimeout(timeout)
          }, 1000)
        }
      }
    }

    if (autoShowTooltip) {
      handleTooltipAutoOpen()

      const timeout = setTimeout(() => {
        setShow(false)
        setDisableAutoTooltip(true)
        clearTimeout(timeout)
      }, 4000)
    }
  }, [autoShowTooltip, id])

  useEffect(() => {
    if (show) {
      tooltipRootRef.current?.classList.add('open')
    } else {
      tooltipRootRef.current?.classList.remove('open')
    }
  }, [show])

  useEffect(() => {
    if (!tooltipRootRef.current) {
      const tooltipRoot = document.getElementById('tooltip-root')
      if (tooltipRoot) tooltipRootRef.current = tooltipRoot
    }
  }, [])

  const tooltipUI = (
    <TooltipStyled
      align={align}
      className={`fixed top-0 z-50 w-max max-w-52 rounded-2xl bg-grey-700 px-5 py-3 text-white ${className}`}
      margin={margin}
      position={position}
      ref={tooltipRef}
      style={{ top: position.y, left: position.x, boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' }}
    >
      <div className="font-medium text-p-sm leading-160">{text}</div>
      <TooltipArrow align={align} />
    </TooltipStyled>
  )

  const mouseEvent = isOnMouseOver ? 'onMouseOver' : 'onMouseEnter'
  const props = {
    ...(isToggledByClick
      ? { onClick: handleTooltipOpen }
      : { [mouseEvent]: handleTooltipOpen, onMouseLeave: () => setShow(false) }),
  }

  return (
    <>
      {show && !disabled && !disableAutoTooltip && tooltipRootRef.current
        ? createPortal(tooltipUI, tooltipRootRef.current)
        : null}
      {cloneElement(children, props)}
    </>
  )
}

const TooltipStyled = styled.div<{ align: string; margin: number; position: { x: number; y: number } }>`
  transform: ${({ align, margin }) =>
    `${
      align === 'bottom' ? `translate(-50%, calc(100%  + ${margin}px))` : `translate(-50%, calc(-50% - ${margin}px))`
    }`};
`

export const TooltipArrow = styled.div<{ align: string }>`
  position: absolute;
  z-index: -1;
  left: 50%;
  width: 1.75rem;
  height: 1.75rem;
  background-color: #1b1918;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transform: ${({ align }) =>
    `${align === 'bottom' ? 'translate(-50%, -60px) rotate(45deg)' : 'translate(-50%, -9px) rotate(45deg)'}`};
`
